/**
 * /22sports/admin — Founder-only roster for the Dunk Jam RSVP.
 * Live counts (yes / maybe / no + head-count for jerseys) + delete.
 */
import React, { useEffect, useState } from 'react';
import { Check, X, HelpCircle, Trash2, Users, Trophy, RefreshCw, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const TAG = 'text-[10px] font-black uppercase tracking-[0.28em]';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className={`border-2 ${color} p-4 rounded-lg`}>
    <div className="flex items-center justify-between mb-1">
      <span className={TAG + ' text-white/60'}>{label}</span>
      <Icon className="w-4 h-4 opacity-80" />
    </div>
    <div className="text-3xl font-black">{value}</div>
  </div>
);

const AttendPill = ({ v }) => {
  if (v === 'yes')   return <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 rounded">✓ Yes</span>;
  if (v === 'no')    return <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 border border-rose-500/40 text-rose-300 bg-rose-500/10 rounded">✗ No</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 border border-amber-500/40 text-amber-300 bg-amber-500/10 rounded">? Maybe</span>;
};

export default function B2RsvpAdmin() {
  const [rsvps, setRsvps] = useState([]);
  const [totals, setTotals] = useState({ yes: 0, no: 0, maybe: 0, total: 0, total_heads: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/rsvp/b2/admin', { headers: authHeaders() });
      setRsvps(data.rsvps || []);
      setTotals(data.totals || totals);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Admin login required — sign in via /academy/login first');
      } else {
        toast.error('Could not load RSVPs');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete RSVP for ${name}?`)) return;
    try {
      await api.delete(`/rsvp/b2/admin/${id}`, { headers: authHeaders() });
      setRsvps(rsvps.filter((r) => r.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className={TAG + ' text-orange-400 mb-1'}>Dunk Jam Roster</div>
            <h1 className="text-3xl font-black">B2's 13th Birthday · RSVPs</h1>
            <p className="text-white/50 text-sm mt-1">October 9, 2026 · Handle Life Academy</p>
          </div>
          <button
            onClick={() => { setRefreshing(true); load(); }}
            className="flex items-center gap-2 text-xs uppercase tracking-widest font-black px-3 py-2 border border-white/20 hover:border-orange-500 rounded"
            data-testid="b2-admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Coming" value={totals.yes} icon={Check} color="border-emerald-500/40 bg-emerald-500/5 text-emerald-300" />
          <StatCard label="Maybe" value={totals.maybe} icon={HelpCircle} color="border-amber-500/40 bg-amber-500/5 text-amber-300" />
          <StatCard label="Can't" value={totals.no} icon={X} color="border-rose-500/40 bg-rose-500/5 text-rose-300" />
          <StatCard label="Head Count" value={totals.total_heads} icon={Users} color="border-orange-500/40 bg-orange-500/5 text-orange-300" />
        </div>

        {rsvps.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-white/10 rounded-xl">
            <Trophy className="w-10 h-10 mx-auto text-white/20 mb-3" />
            <p className="text-white/50">No RSVPs yet. Share <span className="text-orange-400 font-semibold">scholarathlete.academy/22sports</span> and watch them roll in.</p>
          </div>
        ) : (
          <div className="border-2 border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr className={TAG + ' text-white/50'}>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">RSVP</th>
                    <th className="text-left py-3 px-4">+Guests</th>
                    <th className="text-right py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`b2-row-${r.id}`}>
                      <td className="py-3 px-4 font-semibold text-white">{r.name}</td>
                      <td className="py-3 px-4"><AttendPill v={r.attending} /></td>
                      <td className="py-3 px-4 text-white/70">{r.guests || 0}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => remove(r.id, r.name)}
                          className="text-rose-400 hover:text-rose-300 p-1"
                          data-testid={`b2-delete-${r.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
